import { ValidationResult } from 'react-aria-components';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden';
import FieldDescription from '../FieldDescription/FieldDescription';
import FieldError from '../FieldError/FieldError';
import { FieldLabel } from '../FieldLabel/FieldLabel';
import styles from './FieldHeader.module.css';
import { forwardRef } from 'react';

export interface FieldHeaderProps {
  /*
   * The label for the FieldHeader
   */
  label?: string;
  /*
   * The description for the FieldHeader
   */
  description?: string;
  /*
   * The error message for the FieldHeader
   */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /*
   * Whether the FieldHeader is invalid
   */
  isInvalid?: boolean;
  /*
   * Whether the FieldHeader is required
   */
  isRequired?: boolean;
  /*
   * The id of the element the label is associated with
   */
  labelFor?: string;
  /*
   * The id of the label element
   */
  labelId?: string;
  /*
   * The id of the description element
   */
  descriptionId?: string;
  /*
   * The id of the error message element
   */
  errorId?: string;
  /*
   * Optional translation for the Optional text, used for localization
   * @default 'Optional'
   */
  optionalTranslation?: string;
  /*
   * Whether to show the error in this header (for error placement control)
   * @default true
   */
  showError?: boolean;
  /*
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * Additional context for screen readers, rendered visually hidden next to the label.
   */
  visuallyHiddenLabel?: string;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export const FieldHeader = forwardRef<HTMLDivElement, FieldHeaderProps>(
  (
    {
      label,
      description,
      isInvalid,
      isRequired,
      errorMessage,
      labelFor,
      labelId,
      descriptionId,
      errorId,
      optionalTranslation = 'Optional',
      showError = true,
      errorPrefix,
      visuallyHiddenLabel,
      'data-testid': dataTestId,
    },
    forwardedRef,
  ) => {
    return (
      <div
        className={styles.fieldHeader}
        data-invalid={isInvalid ? true : undefined}
        ref={forwardedRef}
        data-testid={dataTestId}
      >
        <div className={styles['label-container']}>
          {label && (
            <FieldLabel
              required={isRequired ? true : undefined}
              htmlFor={labelFor}
              id={labelId}
            >
              {label}
              {!isRequired && ` (${optionalTranslation})`}
              {visuallyHiddenLabel && (
                <VisuallyHidden>{visuallyHiddenLabel}</VisuallyHidden>
              )}
            </FieldLabel>
          )}
        </div>
        {description && (
          <FieldDescription id={descriptionId}>{description}</FieldDescription>
        )}
        {showError && (
          <FieldError id={errorId} errorPrefix={errorPrefix} fieldLabel={label}>
            {errorMessage}
          </FieldError>
        )}
      </div>
    );
  },
);
FieldHeader.displayName = 'FieldHeader';

export default FieldHeader;
